import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { ReviewService, OtpService } from '../services/reviewService';
import { Review, ReviewFormData, UserDetails } from '../types/review';
import TimeInProperty from './TimeInProperty';
import StarRating from './StarRating';
import ImageUpload from './ImageUpload';
import CouncilHousingSearchInput from './CouncilHousingSearchInput';
import PrivateCompanySearchInput from './PrivateCompanySearchInput';
import LettingAgentSearchInput from './LettingAgentSearchInput';
import StudentAccommodationSearchInput from './StudentAccommodationSearchInput';

interface ReviewModalProps {
  showReviewModal: boolean;
  setShowReviewModal: (show: boolean) => void;
  showReviewForm: boolean;
  setShowReviewForm: (show: boolean) => void;
  hasLandlordInfo: boolean;
  setHasLandlordInfo: (has: boolean) => void;
  userDetails: UserDetails;
  setUserDetails: (details: UserDetails) => void;
  newReview: ReviewFormData;
  setNewReview: (review: ReviewFormData) => void;
  getAddressAndUdprn: () => { address: string; udprn: string | null };
  onReviewSubmitted: (review: Review) => void;
  isSubmitting?: boolean;
  onCancel: () => void;
}

type ModerationStatus = 'idle' | 'pending' | 'pass' | 'fail' | 'error';

const ReviewModal: React.FC<ReviewModalProps> = ({
  showReviewModal,
  setShowReviewModal,
  showReviewForm,
  setShowReviewForm,
  hasLandlordInfo,
  setHasLandlordInfo,
  userDetails,
  setUserDetails,
  newReview,
  setNewReview,
  getAddressAndUdprn,
  onReviewSubmitted,
  isSubmitting = false,
  onCancel,
}) => {
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [propertyReviewModerationStatus, setPropertyReviewModerationStatus] =
    useState<ModerationStatus>('idle');
  const [locationReviewModerationStatus, setLocationReviewModerationStatus] =
    useState<ModerationStatus>('idle');
  const [landlordReviewModerationStatus, setLandlordReviewModerationStatus] =
    useState<ModerationStatus>('idle');
  const [agentReviewModerationStatus, setAgentReviewModerationStatus] =
    useState<ModerationStatus>('idle');

  // Phone verification states
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isNumberVerified, setIsNumberVerified] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);

  // Auto-calculate move-in date based on time in property
  useEffect(() => {
    const calculateMoveInDate = () => {
      const now = new Date();
      const totalMonths =
        newReview.timeInPropertyYears * 12 + newReview.timeInPropertyMonths;

      if (totalMonths > 0) {
        const moveInDate = new Date(
          now.getFullYear(),
          now.getMonth() - totalMonths,
          1
        );
        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];

        const formattedDate = `${
          monthNames[moveInDate.getMonth()]
        } ${moveInDate.getFullYear()}`;
        setNewReview((prev) => ({ ...prev, dateInProperty: formattedDate }));
      } else {
        setNewReview((prev) => ({ ...prev, dateInProperty: '' }));
      }
    };

    calculateMoveInDate();
  }, [newReview.timeInPropertyYears, newReview.timeInPropertyMonths]);

  const handleRequestOtp = async () => {
    if (!userDetails.phone) {
      setOtpMessage('Please enter a valid phone number first.');
      return;
    }

    if (phoneError) {
      setOtpMessage('Please fix the phone number error first.');
      return;
    }

    setIsRequestingOtp(true);
    setOtpMessage('');

    try {
      await OtpService.requestOtp(userDetails.phone);
      setShowOtpSection(true);
      setOtpMessage('OTP sent. Please enter the code below.');
    } catch (error) {
      console.error('Failed to request OTP:', error);
      setOtpMessage('Failed to send OTP. Please try again.');
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const handleSubmitOtp = async () => {
    if (!otpCode.trim()) {
      setOtpMessage('Please enter the OTP code.');
      return;
    }

    setIsSubmittingOtp(true);
    setOtpMessage('');

    try {
      const isValid = await OtpService.verifyOtp(userDetails.phone!, otpCode);

      if (isValid) {
        setIsNumberVerified(true);
        setOtpMessage('Number verified!');
        setShowOtpSection(false);
        setOtpCode('');
      } else {
        setOtpMessage('Invalid code. Please try again.');
      }
    } catch (error) {
      console.error('Failed to verify OTP:', error);
      setOtpMessage('Verification failed. Please try again.');
    } finally {
      setIsSubmittingOtp(false);
    }
  };

  const handleReviewModeration = async (
    text: string,
    setModerationStatus: (status: ModerationStatus) => void
  ) => {
    if (!text.trim()) {
      setModerationStatus('idle');
      return;
    }

    setModerationStatus('pending');

    try {
      const moderationKey = import.meta.env.VITE_MODERATION_KEY;
      console.log(
        'Moderation Key:',
        moderationKey ? `${moderationKey.substring(0, 10)}...` : 'undefined'
      );
      if (!moderationKey) {
        console.error('VITE_MODERATION_KEY not configured');
        setModerationStatus('error');
        return;
      }

      console.log('Making moderation request with API key');
      const params = new URLSearchParams({
        api_key: moderationKey,
        text: text.trim(),
      });
      const apiUrl = `https://hgrnnuegfkuqyyvzjxfn.supabase.co/functions/v1/text-moderation?${params.toString()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
      });

      console.log('Moderation response status:', response.status);
      if (!response.ok) {
        throw new Error(`Moderation request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('Moderation response data:', data);

      if (data.status === 'pass') {
        setModerationStatus('pass');
      } else {
        setModerationStatus('fail');
      }
    } catch (error) {
      console.error('Error during text moderation:', error);
      setModerationStatus('error');
    }
  };

  const handlePropertyReviewModeration = (text: string) => {
    handleReviewModeration(text, setPropertyReviewModerationStatus);
  };

  const handleLocationReviewModeration = (text: string) => {
    handleReviewModeration(text, setLocationReviewModerationStatus);
  };

  const handleLandlordReviewModeration = (text: string) => {
    handleReviewModeration(text, setLandlordReviewModerationStatus);
  };

  const handleAgentReviewModeration = (text: string) => {
    handleReviewModeration(text, setAgentReviewModerationStatus);
  };

  const renderModerationStatus = (status: ModerationStatus) => {
    if (status === 'idle') return null;

    return (
      <div
        className={`mt-2 text-sm flex items-center gap-2 ${
          status === 'pass'
            ? 'text-green-600'
            : status === 'fail'
            ? 'text-red-600'
            : status === 'error'
            ? 'text-red-600'
            : 'text-gray-600'
        }`}
      >
        {status === 'pending' && (
          <>
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
            Checking content...
          </>
        )}
        {status === 'pass' && (
          <>
            <span className="text-green-600">✓</span>
            Content approved
          </>
        )}
        {status === 'fail' && (
          <>
            <span className="text-red-600">✗</span>
            Content flagged - please revise
          </>
        )}
        {status === 'error' && (
          <>
            <span className="text-red-600">⚠</span>
            Moderation check failed
          </>
        )}
      </div>
    );
  };

  const validatePhoneNumber = (phone: string | undefined) => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }

    const phoneDigits = phone.replace(/[^0-9]/g, '');
    if (phoneDigits.length < 10) {
      setPhoneError('Phone number must have at least 10 digits');
      return false;
    }

    setPhoneError(null);
    return true;
  };

  const handlePhoneChange = (value: string | undefined) => {
    setUserDetails({ ...userDetails, phone: value });
    if (value) {
      validatePhoneNumber(value);
    } else {
      setPhoneError('Phone number is required');
    }
  };

  const handleReviewFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Check if any content has been flagged by moderation
    const moderationStatuses = [
      propertyReviewModerationStatus,
      locationReviewModerationStatus,
      landlordReviewModerationStatus,
      agentReviewModerationStatus,
    ];

    const hasFailedModeration = moderationStatuses.some(
      (status) => status === 'fail'
    );
    const hasPendingModeration = moderationStatuses.some(
      (status) => status === 'pending'
    );

    if (hasFailedModeration) {
      setValidationError(
        'Please revise any flagged content before proceeding.'
      );
      return;
    }

    if (hasPendingModeration) {
      setValidationError(
        'Please wait for content moderation to complete before proceeding.'
      );
      return;
    }

    // Validate star ratings - ensure minimum 0.5 stars for required ratings
    if (newReview.propertyRating === 0) {
      setValidationError(
        'Please provide a property rating of at least 0.5 stars.'
      );
      return;
    }

    if (newReview.locationRating === 0) {
      setValidationError(
        'Please provide a location rating of at least 0.5 stars.'
      );
      return;
    }

    // Check agent rating if letting agent is involved
    if (newReview.hasLettingAgent === true && newReview.agentRating === 0) {
      setValidationError(
        'Please provide a letting agent rating of at least 0.5 stars.'
      );
      return;
    }

    // Check landlord rating for private individual properties
    if (
      newReview.propertyOwnership === 'private-individual' &&
      newReview.landlordRating === 0
    ) {
      setValidationError(
        'Please provide a landlord rating of at least 0.5 stars.'
      );
      return;
    }

    // Validate landlord phone number if provided
    if (newReview.landlordPhone && newReview.landlordPhone.trim()) {
      const phoneDigits = newReview.landlordPhone.replace(/[^0-9]/g, '');
      if (phoneDigits.length < 10) {
        setValidationError(
          'Please enter a valid landlord phone number with at least 10 digits, or leave it empty.'
        );
        return;
      }
    }

    // Validate that property ownership is selected
    if (!newReview.propertyOwnership) {
      setValidationError(
        'Please select who owns your property before proceeding.'
      );
      return;
    }

    // Validate that letting agent question is answered
    if (newReview.hasLettingAgent === null) {
      setValidationError(
        'Please indicate whether a letting agent manages your rental.'
      );
      return;
    }

    // Validate that if they have a letting agent, one is selected
    if (newReview.hasLettingAgent === true && !newReview.lettingAgentName) {
      setValidationError('Please select a letting agent.');
      return;
    }

    // Validate that experienced issues is answered for housing association/council properties
    if (
      (newReview.propertyOwnership === 'housing-association' ||
        newReview.propertyOwnership === 'council') &&
      (!newReview.experiencedIssues || newReview.experiencedIssues.length === 0)
    ) {
      setValidationError(
        'Please indicate whether you have experienced any of the listed issues.'
      );
      return;
    }

    // Validate compensation consent for housing association/council properties with issues
    if (
      (newReview.propertyOwnership === 'housing-association' ||
        newReview.propertyOwnership === 'council') &&
      newReview.experiencedIssues &&
      newReview.experiencedIssues.length > 0 &&
      !newReview.experiencedIssues.includes('none-of-the-above') &&
      newReview.consentCompensationContact === null
    ) {
      setValidationError(
        'Please indicate whether you consent to being contacted about compensation.'
      );
      return;
    }

    // Validate conditional fields
    if (
      newReview.propertyOwnership === 'private-company' &&
      !newReview.privateCompanyName?.trim()
    ) {
      setValidationError('Please enter the private company name.');
      return;
    }

    if (
      newReview.propertyOwnership === 'student-accommodation' &&
      !newReview.studentAccommodationName?.trim()
    ) {
      setValidationError(
        'Please enter the student accommodation provider name.'
      );
      return;
    }

    if (
      newReview.propertyOwnership === 'housing-association' &&
      !newReview.councilHousingRegistrationNumber
    ) {
      setValidationError('Please select a housing association or council.');
      return;
    }

    if (
      newReview.propertyOwnership === 'council' &&
      !newReview.councilHousingRegistrationNumber
    ) {
      setValidationError('Please select a housing association or council.');
      return;
    }

    // If all validations pass, proceed to user details form
    setShowReviewForm(false);
  };

  const handleAddReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate phone number before proceeding
    if (!validatePhoneNumber(userDetails.phone)) {
      return; // Don't proceed if phone validation fails
    }

    // Get current address and UDPRN from URL
    const { address, udprn } = getAddressAndUdprn();

    // Debug logging
    console.log('Review submission - Address:', address);
    console.log('Review submission - UDPRN:', udprn);
    console.log('Review submission - User details:', userDetails);
    console.log('Review submission - Review data:', newReview);

    try {
      // Use a default address if none is provided (for reviews submitted without property selection)
      const reviewAddress =
        address && address.trim() !== '' ? address : 'Address not specified';

      const review = await ReviewService.createReview(
        reviewAddress,
        udprn,
        userDetails,
        newReview
      );

      onReviewSubmitted(review);
    } catch (error) {
      console.error('Failed to submit review:', error);
      setValidationError('Failed to submit review. Please try again.');
    }
  };

  if (!showReviewModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto p-0 md:p-4">
      <div className="bg-gradient-to-b from-[#e8f5e9] to-white w-full h-full rounded-none overflow-y-auto md:max-w-md md:mx-4 md:my-4 md:rounded-3xl md:h-auto md:max-h-[85vh]">
        <div className="p-8 space-y-6">
          {/* STEP 1: Write your review - shown when showReviewForm is TRUE */}
          {showReviewForm ? (
            <>
              <div className="text-center space-y-2">
                <div className="flex justify-center mb-4">
                  <img
                    src="https://rentreview.io/wp-content/uploads/2025/06/Rent-Review-Logo-2.png"
                    alt="RentReview Logo"
                    className="h-16"
                  />
                </div>
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <span className="inline-block">💬</span> By Renters. For
                  Renters
                </div>
              </div>

              <div className="flex justify-center">
                <div className="bg-black text-white text-center py-1.5 px-4 rounded-full text-sm w-32">
                  STEP 1 OF 2
                </div>
              </div>

              <h2 className="text-2xl font-bold text-center">
                Write your review
              </h2>

              <form onSubmit={handleReviewFormSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Who Owns Your Property?*
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="propertyOwnership"
                        value="private-individual"
                        checked={
                          newReview.propertyOwnership === 'private-individual'
                        }
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            propertyOwnership: e.target.value as
                              | 'private-individual'
                              | 'private-company'
                              | 'housing-association'
                              | 'council',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Private Landlord</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="propertyOwnership"
                        value="private-company"
                        checked={
                          newReview.propertyOwnership === 'private-company'
                        }
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            propertyOwnership: e.target.value as
                              | 'private-individual'
                              | 'private-company'
                              | 'housing-association'
                              | 'council'
                              | 'student-accommodation',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Private Company</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="propertyOwnership"
                        value="student-accommodation"
                        checked={
                          newReview.propertyOwnership ===
                          'student-accommodation'
                        }
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            propertyOwnership: e.target.value as
                              | 'private-individual'
                              | 'private-company'
                              | 'housing-association'
                              | 'council'
                              | 'student-accommodation',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Student Accommodation</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="propertyOwnership"
                        value="housing-association"
                        checked={
                          newReview.propertyOwnership === 'housing-association'
                        }
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            propertyOwnership: e.target.value as
                              | 'private-individual'
                              | 'private-company'
                              | 'housing-association'
                              | 'council'
                              | 'student-accommodation',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Housing Association</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="propertyOwnership"
                        value="council"
                        checked={newReview.propertyOwnership === 'council'}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            propertyOwnership: e.target.value as
                              | 'private-individual'
                              | 'private-company'
                              | 'housing-association'
                              | 'council'
                              | 'student-accommodation',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Council</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="propertyOwnership"
                        value="unknown"
                        checked={newReview.propertyOwnership === 'unknown'}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            propertyOwnership: e.target.value as
                              | 'private-individual'
                              | 'private-company'
                              | 'housing-association'
                              | 'council'
                              | 'student-accommodation'
                              | 'unknown',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Not Sure</span>
                    </label>
                  </div>
                </div>

                {newReview.propertyOwnership === 'private-company' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Private Company Name - Enter Name and Location*
                    </label>
                    <PrivateCompanySearchInput
                      onSelectCompany={(
                        companyName,
                        placeId,
                        latitude,
                        longitude,
                        address,
                        website,
                        phone,
                        openHours
                      ) => {
                        setNewReview({
                          ...newReview,
                          privateCompanyName: companyName,
                          privateCompanyId: placeId,
                          privateCompanyLatitude: latitude,
                          privateCompanyLongitude: longitude,
                          privateCompanyAddress: address,
                          privateCompanyWebsite: website,
                          privateCompanyPhone: phone,
                          privateCompanyOpenHours: openHours,
                        });
                      }}
                      initialValue={newReview.privateCompanyName || ''}
                      required={true}
                    />
                  </div>
                )}

                {newReview.propertyOwnership === 'student-accommodation' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Student Accommodation Provider - Enter Name and Location*
                    </label>
                    <StudentAccommodationSearchInput
                      onSelectAccommodation={(
                        accommodationName,
                        placeId,
                        latitude,
                        longitude,
                        address,
                        website,
                        phone,
                        openHours
                      ) => {
                        setNewReview({
                          ...newReview,
                          studentAccommodationName: accommodationName,
                          studentAccommodationId: placeId,
                          studentAccommodationLatitude: latitude,
                          studentAccommodationLongitude: longitude,
                          studentAccommodationAddress: address,
                          studentAccommodationWebsite: website,
                          studentAccommodationPhone: phone,
                          studentAccommodationOpenHours: openHours,
                        });
                      }}
                      initialValue={newReview.studentAccommodationName || ''}
                      required={true}
                    />
                  </div>
                )}

                {(newReview.propertyOwnership === 'housing-association' ||
                  newReview.propertyOwnership === 'council') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Housing Association / Council*
                    </label>
                    <CouncilHousingSearchInput
                      onSelectOrganization={(
                        registrationNumber,
                        organizationName
                      ) => {
                        setNewReview({
                          ...newReview,
                          councilHousingRegistrationNumber: registrationNumber,
                          councilHousingName: organizationName,
                        });
                      }}
                      initialValue={newReview.councilHousingName || ''}
                      required={true}
                      propertyOwnershipType={newReview.propertyOwnership}
                    />
                  </div>
                )}

                {newReview.propertyOwnership === 'private-individual' && (
                  <div className="space-y-4">
                    <p className="text-xs text-gray-500 italic">
                      We will never share your landlord's information publicly.
                    </p>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Landlord's Name - Optional
                      </label>
                      <input
                        type="text"
                        value={newReview.landlordName}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            landlordName: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                        placeholder="Landlord's name..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Landlord's Email - Optional
                      </label>
                      <input
                        type="email"
                        value={newReview.landlordEmail}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            landlordEmail: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                        placeholder="Landlord's email..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Landlord's Phone - Optional
                      </label>
                      <input
                        type="tel"
                        value={newReview.landlordPhone}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            landlordPhone: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                        placeholder="Landlord's phone number..."
                        minLength={10}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional - Minimum 10 digits if provided
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Does a letting agent manage the property?
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="hasLettingAgent"
                        value="yes"
                        checked={newReview.hasLettingAgent === true}
                        onChange={(e) =>
                          setNewReview({ ...newReview, hasLettingAgent: true })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="hasLettingAgent"
                        value="no"
                        checked={newReview.hasLettingAgent === false}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            hasLettingAgent: false,
                            letting_agent_id: '',
                          })
                        }
                        className="text-[#30a86b] focus:ring-[#30a86b]"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                </div>

                {newReview.hasLettingAgent === true && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Letting Agent - Type in your agent and location to
                        ensure you select the right one!
                      </label>
                      <LettingAgentSearchInput
                        onSelectAgent={(
                          agentName,
                          placeId,
                          latitude,
                          longitude,
                          address,
                          website,
                          phone,
                          openHours
                        ) => {
                          setNewReview({
                            ...newReview,
                            lettingAgentName: agentName,
                            lettingAgentPlaceId: placeId,
                            lettingAgentLatitude: latitude,
                            lettingAgentLongitude: longitude,
                            lettingAgentAddress: address,
                            lettingAgentWebsite: website,
                            lettingAgentPhone: phone,
                            lettingAgentOpenHours: openHours,
                          });
                        }}
                        initialValue={newReview.lettingAgentName || ''}
                        required={true}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Time Lived in Property*
                    </label>
                    <TimeInProperty
                      years={newReview.timeInPropertyYears}
                      months={newReview.timeInPropertyMonths}
                      onYearsChange={(years) =>
                        setNewReview({
                          ...newReview,
                          timeInPropertyYears: years,
                        })
                      }
                      onMonthsChange={(months) =>
                        setNewReview({
                          ...newReview,
                          timeInPropertyMonths: months,
                        })
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      A rough estimate is fine!
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Move in date
                    </label>
                    <input
                      type="text"
                      value={newReview.dateInProperty}
                      onChange={(e) =>
                        setNewReview({
                          ...newReview,
                          dateInProperty: e.target.value,
                        })
                      }
                      placeholder="e.g. January 2020"
                      className="w-full p-3 border border-gray-300 rounded-xl bg-white text-gray-900"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-calculated from time in property, but you can edit if
                      needed
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <StarRating
                        rating={newReview.propertyRating}
                        onRatingChange={(value) =>
                          setNewReview({ ...newReview, propertyRating: value })
                        }
                        label="Property Rating"
                      />
                      <textarea
                        value={newReview.property_review}
                        onChange={(e) =>
                          setNewReview({
                            ...newReview,
                            property_review: e.target.value,
                          })
                        }
                        onBlur={(e) =>
                          handlePropertyReviewModeration(e.target.value)
                        }
                        placeholder="Tell us about the property condition, features, and any issues..."
                        className="w-full p-3 border border-gray-300 rounded-xl bg-white h-24"
                        required
                      />
                      {renderModerationStatus(propertyReviewModerationStatus)}
                    </div>

                    <StarRating
                      rating={newReview.locationRating}
                      onRatingChange={(value) =>
                        setNewReview({ ...newReview, locationRating: value })
                      }
                      label="Location Rating"
                    />
                    <textarea
                      value={newReview.location_review}
                      onChange={(e) =>
                        setNewReview({
                          ...newReview,
                          location_review: e.target.value,
                        })
                      }
                      onBlur={(e) =>
                        handleLocationReviewModeration(e.target.value)
                      }
                      placeholder="Tell us about the area, transport links, and local amenities..."
                      className="w-full p-3 border border-gray-300 rounded-xl bg-white h-24"
                      required
                    />
                    {renderModerationStatus(locationReviewModerationStatus)}

                    <StarRating
                      rating={newReview.landlordRating}
                      onRatingChange={(value) =>
                        setNewReview({ ...newReview, landlordRating: value })
                      }
                      label="Landlord Rating"
                    />
                    {newReview.propertyOwnership && (
                      <div className="text-xs text-gray-500 mb-2">
                        Your landlord is:{' '}
                        {newReview.propertyOwnership === 'private-individual'
                          ? 'Private Landlord'
                          : newReview.propertyOwnership === 'private-company'
                          ? 'Private Company'
                          : newReview.propertyOwnership ===
                            'housing-association'
                          ? 'Housing Association'
                          : newReview.propertyOwnership === 'council'
                          ? 'Council'
                          : newReview.propertyOwnership ===
                            'student-accommodation'
                          ? 'Student Accommodation'
                          : newReview.propertyOwnership === 'unknown'
                          ? 'Unknown'
                          : newReview.propertyOwnership}
                      </div>
                    )}
                    <textarea
                      value={newReview.landlord_review}
                      onChange={(e) =>
                        setNewReview({
                          ...newReview,
                          landlord_review: e.target.value,
                        })
                      }
                      onBlur={(e) =>
                        handleLandlordReviewModeration(e.target.value)
                      }
                      placeholder="Tell us about your experience with the landlord..."
                      className="w-full p-3 border border-gray-300 rounded-xl bg-white h-24"
                    />
                    {renderModerationStatus(landlordReviewModerationStatus)}

                    {newReview.hasLettingAgent === true && (
                      <>
                        <StarRating
                          rating={newReview.agentRating}
                          onRatingChange={(value) =>
                            setNewReview({ ...newReview, agentRating: value })
                          }
                          label="Letting Agent Rating"
                        />
                        <textarea
                          value={newReview.agent_review}
                          onChange={(e) =>
                            setNewReview({
                              ...newReview,
                              agent_review: e.target.value,
                            })
                          }
                          onBlur={(e) =>
                            handleAgentReviewModeration(e.target.value)
                          }
                          placeholder="Tell us about your experience with the letting agent..."
                          className="w-full p-3 border border-gray-300 rounded-xl bg-white h-24"
                          required={newReview.hasLettingAgent === true}
                        />
                        {renderModerationStatus(agentReviewModerationStatus)}
                      </>
                    )}
                  </div>

                  {(newReview.propertyOwnership === 'housing-association' ||
                    newReview.propertyOwnership === 'council') && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Have you experienced any of the below issues?*
                        </label>
                        <div className="space-y-3">
                          {[
                            { value: 'mould-damp', label: 'Mould/Damp' },
                            {
                              value: 'active-leaks-flooding',
                              label: 'Active Leaks/ Flooding',
                            },
                            {
                              value: 'drainage-issues',
                              label: 'Drainage Issues',
                            },
                            {
                              value: 'faulty-drafty-windows-doors',
                              label: 'Faulty/Drafty Windows or Doors',
                            },
                            {
                              value: 'rodent-infestation',
                              label: 'Rodent Infestation (Rats, Insects etc)',
                            },
                            {
                              value: 'crumbling-plaster-ceilings',
                              label: 'Crumbling Plaster / Ceilings',
                            },
                            {
                              value: 'electric-faults',
                              label: 'Electric Faults',
                            },
                            {
                              value: 'defective-boiler-heating',
                              label: 'Defective Boiler or Heating',
                            },
                            {
                              value: 'none-of-the-above',
                              label: 'None of the above',
                            },
                          ].map((issue) => (
                            <label
                              key={issue.value}
                              className="flex items-center gap-3"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  newReview.experiencedIssues?.includes(
                                    issue.value
                                  ) || false
                                }
                                onChange={(e) => {
                                  const currentIssues =
                                    newReview.experiencedIssues || [];
                                  let updatedIssues: string[];

                                  if (issue.value === 'none-of-the-above') {
                                    // If "None of the above" is selected, clear all other issues
                                    updatedIssues = e.target.checked
                                      ? ['none-of-the-above']
                                      : [];
                                  } else {
                                    // If any other issue is selected, remove "None of the above"
                                    const filteredIssues = currentIssues.filter(
                                      (i) => i !== 'none-of-the-above'
                                    );

                                    if (e.target.checked) {
                                      updatedIssues = [
                                        ...filteredIssues,
                                        issue.value,
                                      ];
                                    } else {
                                      updatedIssues = filteredIssues.filter(
                                        (i) => i !== issue.value
                                      );
                                    }
                                  }

                                  setNewReview({
                                    ...newReview,
                                    experiencedIssues: updatedIssues,
                                  });
                                }}
                                className="text-[#30a86b] focus:ring-[#30a86b]"
                                required={
                                  !newReview.experiencedIssues ||
                                  newReview.experiencedIssues.length === 0
                                }
                              />
                              <span className="text-sm">{issue.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {newReview.experiencedIssues &&
                        newReview.experiencedIssues.length > 0 &&
                        !newReview.experiencedIssues.includes(
                          'none-of-the-above'
                        ) && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">
                                You may be eligible to £1500-£3000+ in
                                compensation due to these issues, is it ok for a
                                third party to contact you regarding this?*
                              </label>
                              <div className="space-y-3">
                                <label className="flex items-center gap-3">
                                  <input
                                    type="radio"
                                    name="consentCompensationContact"
                                    value="yes"
                                    checked={
                                      newReview.consentCompensationContact ===
                                      true
                                    }
                                    onChange={(e) =>
                                      setNewReview({
                                        ...newReview,
                                        consentCompensationContact: true,
                                      })
                                    }
                                    className="text-[#30a86b] focus:ring-[#30a86b]"
                                    required={
                                      newReview.consentCompensationContact ===
                                      null
                                    }
                                  />
                                  <span className="text-sm font-medium">
                                    Yes
                                  </span>
                                </label>
                                <label className="flex items-center gap-3">
                                  <input
                                    type="radio"
                                    name="consentCompensationContact"
                                    value="no"
                                    checked={
                                      newReview.consentCompensationContact ===
                                      false
                                    }
                                    onChange={(e) =>
                                      setNewReview({
                                        ...newReview,
                                        consentCompensationContact: false,
                                      })
                                    }
                                    className="text-[#30a86b] focus:ring-[#30a86b]"
                                    required={
                                      newReview.consentCompensationContact ===
                                      null
                                    }
                                  />
                                  <span className="text-sm font-medium">
                                    No
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Upload Images
                    </label>
                    <ImageUpload
                      images={newReview.images || []}
                      onImagesChange={(images) =>
                        setNewReview((prev) => ({ ...prev, images }))
                      }
                      maxImages={10}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="flex flex-col items-center gap-4 pt-4">
                  {validationError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full">
                      <div className="text-red-800 text-sm font-medium">
                        {validationError}
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      propertyReviewModerationStatus === 'fail' ||
                      locationReviewModerationStatus === 'fail' ||
                      landlordReviewModerationStatus === 'fail' ||
                      agentReviewModerationStatus === 'fail' ||
                      propertyReviewModerationStatus === 'pending' ||
                      locationReviewModerationStatus === 'pending' ||
                      landlordReviewModerationStatus === 'pending' ||
                      agentReviewModerationStatus === 'pending'
                    }
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl w-full justify-center font-semibold ${
                      isSubmitting ||
                      propertyReviewModerationStatus === 'fail' ||
                      locationReviewModerationStatus === 'fail' ||
                      landlordReviewModerationStatus === 'fail' ||
                      agentReviewModerationStatus === 'fail' ||
                      propertyReviewModerationStatus === 'pending' ||
                      locationReviewModerationStatus === 'pending' ||
                      landlordReviewModerationStatus === 'pending' ||
                      agentReviewModerationStatus === 'pending'
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    Continue <ArrowRight className="w-5 h-5" />
                  </button>
                  <div className="text-xs text-gray-600 text-center">
                    By submitting a review, you confirm that it is truthful,
                    accurate, and complies with all our{' '}
                    <a
                      href="/terms-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      T&Cs
                    </a>
                    .
                  </div>
                  <button
                    type="button"
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          ) : (
            /* STEP 2: Share your details - shown when showReviewForm is FALSE */
            <>
              <div className="text-center space-y-2">
                <div className="flex justify-center mb-4">
                  <img
                    src="https://rentreview.io/wp-content/uploads/2025/06/Rent-Review-Logo-2.png"
                    alt="RentReview Logo"
                    className="h-16"
                  />
                </div>
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <span className="inline-block">💬</span> By Renters. For
                  Renters
                </div>
              </div>

              <div className="flex justify-center">
                <div className="bg-black text-white text-center py-1.5 px-4 rounded-full text-sm w-32">
                  STEP 2 OF 2
                </div>
              </div>

              <h2 className="text-2xl font-bold text-center">
                Share your details
              </h2>

              <form onSubmit={handleAddReview} className="space-y-6">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name*
                    </label>
                    <input
                      type="text"
                      value={userDetails.name}
                      onChange={(e) =>
                        setUserDetails({ ...userDetails, name: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                      placeholder="Your Full Name..."
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="anonymous"
                      checked={userDetails.isAnonymous}
                      onChange={(e) =>
                        setUserDetails({
                          ...userDetails,
                          isAnonymous: e.target.checked,
                        })
                      }
                      className="rounded text-[#30a86b] focus:ring-[#30a86b]"
                    />
                    <label
                      htmlFor="anonymous"
                      className="text-sm text-gray-700"
                    >
                      I want to hide my name on the review
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Email*
                    </label>
                    <input
                      type="email"
                      value={userDetails.email}
                      onChange={(e) =>
                        setUserDetails({
                          ...userDetails,
                          email: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                      placeholder="Your Email..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Phone Number*
                    </label>
                    <div className="space-y-2">
                      <PhoneInput
                        international
                        defaultCountry="GB"
                        value={userDetails.phone}
                        onChange={handlePhoneChange}
                        className="w-full"
                        required
                      />
                      <button
                        type="button"
                        onClick={handleRequestOtp}
                        disabled={
                          isRequestingOtp ||
                          isNumberVerified ||
                          !userDetails.phone ||
                          !!phoneError
                        }
                        className={`w-full px-4 py-2 rounded-lg text-sm font-medium ${
                          isNumberVerified
                            ? 'bg-green-500 text-white cursor-not-allowed'
                            : isRequestingOtp ||
                              !userDetails.phone ||
                              !!phoneError
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-[#30a86b] text-white hover:bg-[#2a9760]'
                        }`}
                      >
                        {isRequestingOtp
                          ? 'Sending...'
                          : isNumberVerified
                          ? 'Verified'
                          : 'Verify Number'}
                      </button>
                    </div>
                    {phoneError ? (
                      <p className="text-xs text-red-500 mt-1">{phoneError}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1"></p>
                    )}

                    {showOtpSection && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Enter Verification Code
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value)}
                            placeholder="Enter 6-digit code"
                            maxLength={6}
                            className="flex-1 p-3 border border-gray-300 rounded-xl bg-white"
                          />
                          <button
                            type="button"
                            onClick={handleSubmitOtp}
                            disabled={isSubmittingOtp || !otpCode.trim()}
                            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                              isSubmittingOtp || !otpCode.trim()
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-[#30a86b] text-white hover:bg-[#2a9760]'
                            }`}
                          >
                            {isSubmittingOtp ? 'Verifying...' : 'Submit Code'}
                          </button>
                        </div>
                      </div>
                    )}

                    {otpMessage && (
                      <div
                        className={`mt-2 p-2 rounded text-sm ${
                          otpMessage.includes('verified') ||
                          otpMessage.includes('sent')
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                      >
                        {otpMessage}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-4 pt-4">
                  {validationError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full">
                      <div className="text-red-800 text-sm font-medium">
                        {validationError}
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting || !isNumberVerified}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl w-full justify-center font-semibold ${
                      isSubmitting || !isNumberVerified
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    {isSubmitting
                      ? 'Submitting...'
                      : !isNumberVerified
                      ? 'Verify Phone Number First'
                      : 'Submit Review'}{' '}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(true)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={onCancel}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
